import { Component, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DatabaseModelService } from '../../../services/database-model/database-model.service';
import { TableDoesntExistValidator } from '../../../validators/table-doesnt-exist/table-doesnt-exists.validator';
import { RelationType } from '../../../models/enum/relation-type.enum';
import { Observable } from 'rxjs';
import { Link } from '../../../models/class/link.class';
import { AsyncPipe } from '@angular/common';
import { LinkForm } from './../../../models/type/link-form.type';
import { DoubleTableTitle } from '../../../models/type/double-table-title.type';
import { LinkAlreadyExistsValidator } from '../../../validators/link-already-exists/link-already-exists.validator';

@Component({
  selector: 'dbm-link-form',
  standalone: true,
  imports: [ReactiveFormsModule, AsyncPipe],
  templateUrl: './link-form.component.html',
  styleUrls: ['./link-form.component.scss', './../dbm-form.style.scss'],
})
export class LinkFormComponent {
  private _formBuilder: FormBuilder = inject(FormBuilder);
  private _modelService: DatabaseModelService = inject(DatabaseModelService);
  private _tableValidator: TableDoesntExistValidator = inject(TableDoesntExistValidator);
  tables$: Observable<string[]> = this._modelService.getTablesNames$();
  linksValidator: LinkAlreadyExistsValidator = inject(LinkAlreadyExistsValidator);

  relationTypeList: RelationType[] = Object.values(RelationType);
  isEnglish: boolean = false;
  sentence: string = '';

  linkForm: LinkForm = this._initializeForm();
  sentenceCouldBeComputed: boolean = this.linkForm.status === 'VALID';

  onSubmit(): void {
    if (this.linkForm.valid) {
      const link: Link = new Link(
        this.linkForm.value.tables as DoubleTableTitle,
        this.linkForm.value.action as string,
        this.linkForm.value.relationType as RelationType
      );
      this._modelService.addLink$(link);
      this.linkForm.reset();
    }
  }

  tablesAreDifferentsValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const table1 = formGroup.get('table1')?.value;
      const table2 = formGroup.get('table2')?.value;
      return table1 !== table2 ? null : { namesMatch: 'Les tables doivent être différentes.' };
    };
  }

  toggleLang(): void {
    this.isEnglish = !this.isEnglish;
    this.watchChangeInForm();
  }

  watchChangeInForm(): void {
    if (this.linkForm.controls.relationType.valid) this.computeSentence();
  }

  computeSentence(): void {
    const table1: string = this.linkForm.controls.tables.getRawValue().table1;
    const table2: string = this.linkForm.controls.tables.getRawValue().table2;
    const action: string = this.linkForm.controls.action.getRawValue() as string;
    const link: Link = new Link({ table1: table1, table2: table2 }, action, this.linkForm.controls.relationType.value as RelationType);
    this.sentence = link.computeTranslatedSentence(this.isEnglish);
  }

  private _initializeForm(): LinkForm {
    return this._formBuilder.nonNullable.group({
      tables: this._formBuilder.nonNullable.group(
        {
          table1: ['', [Validators.required], [this._tableValidator.validate.bind(this._tableValidator)], 'blur'],
          table2: ['', [Validators.required], [this._tableValidator.validate.bind(this._tableValidator)], 'blur'],
        },
        {
          validators: this.tablesAreDifferentsValidator(),
          asyncValidators: this.linksValidator.validate.bind(this.linksValidator),
        }
      ),

      action: ['', [Validators.required]],
      relationType: ['', [Validators.required]],
    });
  }
}
